// AIDEV-NOTE: Pagina admin para gerenciar produtos (fotos + status de venda).
// Lista produtos do catalogo (sync_produtos via /admin/lista), thumbnails via batch query,
// clique abre Dialog com abas: Fotos (FotoUploader) e Configuracoes (ProdutoConfigTab).

import { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Pagination,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  Search as SearchIcon,
  PhotoLibrary as FotosIcon,
  AddPhotoAlternate as AddFotoIcon,
  ImageNotSupported as NoImageIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/auth.service';
import { useProdutosFotosBatch, useProdutoFotos } from '@/hooks/useProdutoFotos';
import { FotoUploader } from '../components/FotoUploader';
import { ProdutoConfigTab } from '../components/ProdutoConfigTab';
import { ProdutoCarousel } from '@/components/produtos/ProdutoCarousel';

interface Produto {
  CODIGO: number;
  DESCRICAO: string;
  REFERENCIA?: string;
  SECAO?: string;
  GRUPO?: string;
  ativo_venda?: boolean;
}

interface ProdutosApiResponse {
  produtos: Produto[];
  total?: number;
}

// AIDEV-NOTE: Sub-componente do Dialog para evitar buscar fotos de todos os produtos de uma vez.
// Aba Fotos: igual ao original. Aba Configuracoes: ProdutoConfigTab (status + grade).
function GerenciarFotosDialog({ produto, onClose }: { produto: Produto; onClose: () => void }) {
  const [tab, setTab] = useState(0);
  const { data: fotos, isLoading } = useProdutoFotos(produto.CODIGO);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          {produto.DESCRICAO}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Código: {produto.CODIGO}
          {produto.REFERENCIA ? ` · Ref: ${produto.REFERENCIA}` : ''}
        </Typography>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as number)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Fotos" />
        <Tab label="Configurações" />
      </Tabs>

      <DialogContent dividers>
        {tab === 0 && (
          <>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {fotos && fotos.length > 0 && (
                  <Box sx={{ mb: 3, maxWidth: 380, mx: 'auto' }}>
                    <ProdutoCarousel fotos={fotos.map((f) => f.url)} height={260} />
                  </Box>
                )}
                <FotoUploader produtoCodigo={produto.CODIGO} fotos={fotos ?? []} />
              </>
            )}
          </>
        )}
        {tab === 1 && <ProdutoConfigTab produtoCodigo={produto.CODIGO} />}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

const PAGE_SIZE = 20;

type StatusFiltro = 'ativos' | 'inativos' | 'todos';

export function GerenciarFotosPage() {
  const [busca, setBusca] = useState('');
  // AIDEV-NOTE: Default 'ativos' — admin ve por padrao apenas produtos ativos p/ venda
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltro>('ativos');
  const [page, setPage] = useState(1);
  const [produtoSelecionado, setProdutoSelecionado] = useState<Produto | null>(null);

  const buscaTrimmed = busca.trim();

  // Reseta para página 1 ao mudar a busca ou o filtro de status
  useEffect(() => {
    setPage(1);
  }, [buscaTrimmed, statusFiltro]);

  // AIDEV-NOTE: Usa /produtos/admin/lista (DISTINCT ON codigo) para garantir lista completa.
  // Paginado de PAGE_SIZE em PAGE_SIZE — o endpoint retorna total para o componente Pagination.
  const {
    data: response,
    isLoading,
    error
  } = useQuery<ProdutosApiResponse>({
    queryKey: ['produtos-admin-catalogo', buscaTrimmed, statusFiltro, page],
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        status: statusFiltro
      };
      if (buscaTrimmed) params.busca = buscaTrimmed;
      const res = await api.get<ProdutosApiResponse>('/produtos/admin/lista', { params });
      return res.data;
    }
  });

  const produtos = response?.produtos ?? [];
  const total = response?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // AIDEV-PERF: Batch query para thumbnails de todos os produtos da pagina
  const codigos = useMemo(() => produtos.map((p) => p.CODIGO), [produtos]);
  const { data: fotosMap } = useProdutosFotosBatch(codigos);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Produtos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Clique em um produto para gerenciar fotos e configurar visibilidade no catálogo.
      </Typography>

      {/* Busca + filtro de status */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Buscar por nome, referência ou código..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: { xs: '100%', sm: 360 } }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFiltro}
          onChange={(_, v: StatusFiltro | null) => {
            if (v) setStatusFiltro(v);
          }}
        >
          <ToggleButton value="ativos">Ativos</ToggleButton>
          <ToggleButton value="inativos">Inativos</ToggleButton>
          <ToggleButton value="todos">Todos</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading */}
      {isLoading && (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rounded" height={200} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error">
          Erro ao carregar produtos. Verifique sua conexão e tente novamente.
        </Alert>
      )}

      {/* Grid de produtos */}
      {!isLoading && !error && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {total > 0
                ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total} produto${total !== 1 ? 's' : ''}`
                : 'Nenhum produto encontrado'}
              {buscaTrimmed ? ` para "${buscaTrimmed}"` : ''}
            </Typography>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                shape="rounded"
              />
            )}
          </Box>

          {produtos.length === 0 && (
            <Alert severity="info">
              Nenhum produto encontrado{buscaTrimmed ? ` para "${buscaTrimmed}"` : ''}.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: totalPages > 1 ? 0 : undefined }}>
            {produtos.map((produto) => {
              const urls = fotosMap?.[String(produto.CODIGO)] ?? [];
              const temFoto = urls.length > 0;

              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={produto.CODIGO}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 },
                      transition: 'box-shadow 0.2s'
                    }}
                    onClick={() => setProdutoSelecionado(produto)}
                  >
                    {/* Thumbnail */}
                    {temFoto ? (
                      <CardMedia
                        component="img"
                        height={120}
                        image={urls[0]}
                        alt={produto.DESCRICAO}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100'
                        }}
                      >
                        <NoImageIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1, p: 1.5, pb: '8px !important' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.3,
                          fontWeight: 500
                        }}
                      >
                        {produto.DESCRICAO}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        #{produto.CODIGO}
                      </Typography>
                    </CardContent>

                    <CardActions
                      sx={{
                        p: 1,
                        pt: 0,
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 0.5
                      }}
                    >
                      {temFoto ? (
                        <Chip
                          size="small"
                          icon={<FotosIcon sx={{ fontSize: '14px !important' }} />}
                          label={`${urls.length} foto${urls.length !== 1 ? 's' : ''}`}
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<AddFotoIcon sx={{ fontSize: '14px !important' }} />}
                          label="Sem foto"
                          sx={{ fontSize: 11, color: 'text.secondary' }}
                        />
                      )}
                      {produto.ativo_venda === false && (
                        <Chip
                          size="small"
                          label="Inativo"
                          color="error"
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => {
                  setPage(v);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}

      {/* Dialog de gerenciamento */}
      {produtoSelecionado && (
        <GerenciarFotosDialog
          produto={produtoSelecionado}
          onClose={() => setProdutoSelecionado(null)}
        />
      )}
    </Box>
  );
}
